type StateKeys<T> = T extends object
  ? {
      [K in keyof T]-?: K extends string ? [K, ...StateKeys<T[K]>] : never;
    }[keyof T]
  : [];

type Subscriber<T> = keyof T | Partial<StateKeys<T>>;

type SubscriberCallback<T> = (state: T | any) => void;

/**
 * StateManager
 */
export class StateManager<T extends Record<string, any>> {
  private readonly _state: T;
  private readonly subscribers: {
    [K in keyof T]: Array<SubscriberCallback<T[K]>>;
  };
  private readonly diffSubscribers: {
    [K in keyof T]: Array<SubscriberCallback<T[K]>>;
  };

  constructor(initialState: T) {
    this._state = initialState;
    this.subscribers = {} as any;
    this.diffSubscribers = {} as any;
  }

  set(key: keyof T, state: T[keyof T]) {
    if (this.diffSubscribers[key] && this.diffSubscribers[key].length > 0) {
      const diff = this.findDiff(this._state[key], structuredClone(state));
      this.diffSubscribers[key].forEach((callback) => callback(diff));
    }
    this._state[key] = state;

    if (state !== undefined) this.notifySubscribers(key, state);
  }

  private findDiff(obj1: T[keyof T], obj2: T[keyof T]): Record<string, any> | null {
    let diff: Record<string, any> = {};

    if (obj1 && obj2 && typeof obj1 === 'object' && typeof obj2 === 'object') {
      for (const key in obj2) {
        // eslint-disable-next-line no-prototype-builtins
        if (obj2.hasOwnProperty(key)) {
          if (!(key in obj1)) {
            diff[key] = obj2[key];
          } else {
            const newDiff = this.findDiff(obj1[key], obj2[key]);
            if (
              typeof newDiff === 'boolean' ||
              (newDiff !== null && Object.keys(newDiff).length > 0)
            ) {
              diff[key] = newDiff;
            }
          }
        }
      }
    } else if (obj1 !== obj2) {
      diff = obj2;
    }

    if (typeof diff !== 'boolean' && Object.keys(diff).length === 0) return null;
    return diff;
  }

  get<K extends keyof T>(key: K): T[K] {
    if (key !== 'actions') return structuredClone(this._state[key]);
    return this._state[key];
  }

  subscribe<K extends keyof T | Subscriber<T>>(
    _key: K,
    listeners: SubscriberCallback<K>[] | SubscriberCallback<K>,
    binding?: any,
    type: 'diff' | 'all' = 'all',
  ) {
    const subscribers = type === 'diff' ? this.diffSubscribers : this.subscribers;
    const isKeyArray = _key instanceof Array;
    const key = isKeyArray ? _key.join('.') : (_key as keyof T);

    if (!isKeyArray && !(key in this._state))
      return console.warn('No state is available with the key', key);

    if (!(key in subscribers)) subscribers[key] = [];

    if (typeof listeners === 'function') listeners = [listeners];

    listeners.forEach((listener) => {
      const existIndex = subscribers[key].findIndex((cb) => cb.name === 'bound ' + listener.name);
      if (existIndex !== -1) subscribers[key][existIndex] = listener.bind(binding || this);
      else subscribers[key].push(listener.bind(binding || this));
    });
  }

  unsubscribe<K extends keyof T>(key: K, listener: SubscriberCallback<T[K]>) {
    if (key in this.subscribers) {
      const listenerName = 'bound ' + listener.name;
      this.subscribers[key] = this.subscribers[key].filter((cb) => cb.name !== listenerName);
    }
    if (key in this.diffSubscribers) {
      const listenerName = 'bound ' + listener.name;
      this.diffSubscribers[key] = this.diffSubscribers[key].filter(
        (cb) => cb.name !== listenerName,
      );
    }
  }

  notifySubscribers(key: keyof T, state: T[keyof T]) {
    if (this.subscribers[key]) {
      this.subscribers[key].forEach((callback) => callback(state));
    }
  }
}
